import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { IoMdSwap } from "react-icons/io";

import { Calendar } from "@/components/ui/calendar";
import { useDispatch, useSelector } from "react-redux";
import {
  domesticAirports,
  getAvailableFlights,
  getSearchFilters,
  searchAirports,
  setFlightFilters,
  setSearchPayload,
} from "@/src/actions/flightAction";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  HiArrowSmallLeft,
  HiOutlineArrowLongRight,
  HiOutlinePencilSquare,
} from "react-icons/hi2";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import moment from "moment";
import { FaPersonWalkingLuggage } from "react-icons/fa6";
import {
  getUrlParamFromLocation,
  isEmpty,
  isValidDate,
  plainSearchToMyRequestObj,
} from "@/src/helpers/commonHelper";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { MdFlightLand, MdFlightTakeoff } from "react-icons/md";
import { LuChevronDown } from "react-icons/lu";
import { IoMdCalendar, IoMdCloseCircle } from "react-icons/io";
import { PLAIN_SEARCH_FORM } from "@/src/reducers/types";
import { filteredFilters } from "@/src/helpers/flightFilters";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Paths } from "@/src/constants/Paths";
import { Skeleton } from "@/components/ui/skeleton";

export default function FlightsSearchForm({ typeId, fromhomepage }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchForm } = useSelector((state) => state.flights);
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const [openOrigin, setOpenOrigin] = useState(false);
  const [openDest, setOpenDest] = useState(false);
  const [searchFilters, setSearchFilters] = useState(null);
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);
  const [isCalenderOpenReturn, setIsCalenderOpenReturn] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedPaxFareType, setSelectedPaxFareType] = useState(1);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  const [originSearch, setOriginSearch] = useState("");
  const [destSearch, setDestSearch] = useState("");
  const [originResults, setOriginResults] = useState([]);
  const [destResults, setDestResults] = useState([]);
  const [isLoadingOrigin, setIsLoadingOrigin] = useState(false);
  const [isLoadingDest, setIsLoadingDest] = useState(false);


  useEffect(() => {
    dispatch(
      getSearchFilters((filters) => {
        if (!filters) {
          setIsLoadingFilters(false);
          return;
        }


        if (filters.Airports) {
          filters.Airports = filters.Airports.map((air) => ({
            ...air,
            SearchLabel:
              air.IATAcode +
              " - " +
              air.AirportName +
              " " +
              air.AirportCity +
              " " +
              air.Country,
          }));
        }

        setSearchFilters(filters);

        // Initialize selectedPaxFareType with default value (Normal - TypeID: 1)
        const hasParamFareType = searchParams.get("fareType");
        const defaultPaxFareType = filters.FareTypes?.find(
          (pft) => pft.TypeID == (hasParamFareType ?? 1)
        );
        if (defaultPaxFareType) {
          setSelectedPaxFareType(defaultPaxFareType);
        }

        setIsLoadingFilters(false);
      })
    );
  }, [dispatch, searchParams]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (originSearch.length >= 3) {
        setIsLoadingOrigin(true);

        // Filter local domestic airports
        const filteredDomestic = domesticAirports.filter(air =>
          air.IATAcode.toLowerCase().includes(originSearch.toLowerCase()) ||
          air.AirportName.toLowerCase().includes(originSearch.toLowerCase()) ||
          air.AirportCity.toLowerCase().includes(originSearch.toLowerCase())
        ).map(air => ({
          ...air,
          SearchLabel: air.IATAcode + " - " + air.AirportName + " " + air.AirportCity + " " + air.Country
        }));
        //this will search the airport from the database when 3 characters are typed. Airport Code, Place Initials searching
        dispatch(searchAirports(originSearch, (apiResults) => {
          const mappedApi = apiResults.map(air => ({
            ...air,
            SearchLabel: air.IATAcode + " - " + air.AirportName + " " + air.AirportCity + " " + air.Country
          }));

          // Combine local and API results, avoiding duplicates by IATAcode
          const combined = [...filteredDomestic];
          mappedApi.forEach(apiAir => {
            if (!combined.find(d => d.IATAcode === apiAir.IATAcode)) {
              combined.push(apiAir);
            }
          });

          setOriginResults(combined);
          setIsLoadingOrigin(false);
        }));
      } else {
        setOriginResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [originSearch, dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (destSearch.length >= 3) {
        setIsLoadingDest(true);

        // Filter local domestic airports
        const filteredDomestic = domesticAirports.filter(air =>
          air.IATAcode.toLowerCase().includes(destSearch.toLowerCase()) ||
          air.AirportName.toLowerCase().includes(destSearch.toLowerCase()) ||
          air.AirportCity.toLowerCase().includes(destSearch.toLowerCase())
        ).map(air => ({
          ...air,
          SearchLabel: air.IATAcode + " - " + air.AirportName + " " + air.AirportCity + " " + air.Country
        }));

        dispatch(searchAirports(destSearch, (apiResults) => {
          const mappedApi = apiResults.map(air => ({
            ...air,
            SearchLabel: air.IATAcode + " - " + air.AirportName + " " + air.AirportCity + " " + air.Country
          }));

          // Combine local and API results, avoiding duplicates by IATAcode
          const combined = [...filteredDomestic];
          mappedApi.forEach(apiAir => {
            if (!combined.find(d => d.IATAcode === apiAir.IATAcode)) {
              combined.push(apiAir);
            }
          });

          setDestResults(combined);
          setIsLoadingDest(false);
        }));
      } else {
        setDestResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [destSearch, dispatch]);

  const allOriginAirports =
    originSearch.length < 3
      ? searchFilters?.Airports || []
      : originResults;

  const allDestAirports =
    destSearch.length < 3
      ? searchFilters?.Airports || []
      : destResults;


  const formSchema = z
    .object({
      from: z.string().min(2, {
        message: "Origin is required",
      }),
      to: z.string().min(2, {
        message: "Destination is required",
      }),
      departure: z.date({
        invalid_type_error: "Departure date is required",
        required_error: "Date is required",
      }),
      return: z.any(),
      noOfPassenger: z.number(),
      child: z.number(),
      infant: z.number(),
      cabinClass: z.string(),
      tripType: z.string().min(1, {
        message: "Flighttype is requireed",
      }),
      nationality: z.string(),
      fareType: z.number(),
    })
    .superRefine((item, refinementContext) => {
      if (item.tripType == 2 && item.return == "") {
        return refinementContext.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Return date is required in Two ways",
          path: ["return"],
        });
      }
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: searchForm
      ? {
        tripType: searchForm.tripType,
        from: searchForm.from,
        to: searchForm.to,
        departure: new Date(searchForm.departure),
        return: new Date(searchForm.return),
        nationality: searchForm.nationality,
        noOfPassenger: searchForm.noOfPassenger,
        child: searchForm.child,
        infant: searchForm.infant,
        cabinClass: searchForm.cabinClass,
      }
      : {
        tripType: "1",
        from: "",
        to: "",
        departure: "",
        return: "",
        nationality: "NP",
        noOfPassenger: 1,
        child: 0,
        infant: 0,
        cabinClass: "Y",
        fareType: 1,
      },
  });

  useEffect(() => {
    form.setValue("tripType", typeId ?? "1");
  }, [typeId, form]);

  // Handle PaxFareType selection to show DisplayMessage
  const handlePaxFareTypeChange = (value) => {
    form.setValue("fareType", Number(value));
    const selectedType = searchFilters?.FareTypes?.find(
      (pft) => pft.TypeID == value
    );
    setSelectedPaxFareType(selectedType);
  };

  const departureDateChange = form.watch("departure");

  useEffect(() => {
    if (typeId == "2" && form.getValues("return") == "") {
      form.setValue("return", form.getValues("departure"));
    }
  }, [departureDateChange, form, typeId]);

  useEffect(() => {
    if (
      location.pathname === Paths.ROUTE_FLIGHTS_LIST ||
      (location.pathname === Paths.ROUTE_FLIGHTS_LIST_DOMESTIC &&
        typeof searchParams.get("from") != "undefined")
    ) {
      if (searchParams.size > 0) {
        let OfferId = searchParams.get("offerId");
        let searchParamObj = getUrlParamFromLocation(searchParams.toString());
        dispatch({
          type: PLAIN_SEARCH_FORM,
          payload: searchParamObj,
        });
        let postData = plainSearchToMyRequestObj(searchParamObj);
        dispatch(setSearchPayload(postData));

        form.resetField("child");
        form.resetField("infant");
        form.resetField("adult");
        form.setValue("departure", new Date(searchParamObj.departure));
        form.setValue("from", searchParamObj.from);
        form.setValue("to", searchParamObj.to);
        form.setValue("return", searchParamObj.return ? new Date(searchParamObj.return) : "");
        form.setValue("tripType", searchParamObj.tripType);
        form.setValue("fareType", Number(searchParamObj.fareType));

        if (OfferId > 0 || !fromhomepage)
          form.handleSubmit((e) => onSubmit(e))();
        window.scrollTo(0, 0);
      }
    }
  }, [dispatch, form, fromhomepage, searchParams]);

  const onSubmit = (e) => {
    let OfferId = searchParams.get("offerId");
    if (OfferId > 0) {
      e.offerId = OfferId;
    }
    e.departure = isValidDate(e.departure)
      ? moment(e.departure).format("YYYY/MM/DD hh:mm")
      : null;
    e.return =
      new Date(e.return?.toString() ?? "") == "Invalid Date"
        ? moment(e.departure).format("YYYY/MM/DD hh:mm")
        : moment(e.return).format("YYYY/MM/DD hh:mm");

    dispatch({
      type: PLAIN_SEARCH_FORM,
      payload: e,
    });

    const combinedParams = new URLSearchParams(searchParams);
    for (const key in e) {
      if (e[key] !== undefined && e[key] !== null) {
        combinedParams.set(key, e[key]);
      }
    }

    if (searchParams) setSearchParams(combinedParams);

    let domesticPayload = {
      noOfAdult: e.noOfPassenger.toString(),
      noOfChild: e.child.toString(),
      noOfInfant: e.infant.toString(),
      arrivalDate:
        e.tripType == "1" ? "" : moment(e.return).format("DD/MM/YYYY"),
      departureDate: moment(e.departure).format("DD/MM/YYYY"),
      origin: e.from.split("-")[0].trim(),
      destination: e.to.split("-")[0].trim(),
      nationality: e.nationality,
      promoCode: "",
      tripType: e.tripType == "1" ? "O" : "R",
    };

    let isFromDomestic =
      domesticAirports.find((item) => item.IATAcode === e.from) !==
      undefined;
    let isToDomestic =
      domesticAirports.find((item) => item.IATAcode === e.to) !== undefined;
    let isDomesticFlight = isFromDomestic && isToDomestic;

    let postData = plainSearchToMyRequestObj(e);
    dispatch(setSearchPayload(postData));
    searchFlights(isDomesticFlight ? domesticPayload : postData, isDomesticFlight);
    setShowFilter(false);

    if (isDomesticFlight) {
      navigate({
        pathname: Paths.ROUTE_FLIGHTS_LIST_DOMESTIC,
        search: combinedParams.toString(),
      });
    } else {
      navigate({
        pathname: Paths.ROUTE_FLIGHTS_LIST,
        search: combinedParams.toString(),
      });
    }
  };

  const searchFlights = (postData, isDomesticFlight) => {
    window.scrollTo(0, 0);
    if (!fromhomepage) {
      dispatch(
        getAvailableFlights(postData, isDomesticFlight, (response) => {
          if (response.IsSuccess) {
            if (response.Results.length > 0) {
              dispatch(
                setFlightFilters(
                  filteredFilters(response.Results, response.FilterTimes)
                )
              );
            }
          }
        })
      );
    }
  };

  let noOfPassengers =
    form.watch().noOfPassenger +
    form.watch().infant +
    form.watch().child;
  let source = form.watch().from;
  let destination = form.watch().to;
  let departureDate = form.watch().departure;
  let bookClass = form.watch().cabinClass;

  const isFromDomestic =
    domesticAirports.find((item) => item.IATAcode === source) !== undefined;
  const isToDomestic =
    domesticAirports.find((item) => item.IATAcode === destination) !==
    undefined;
  const isDomestic = isFromDomestic && isToDomestic;

  // ─────────────────────────
  // SKELETON WHILE LOADING
  // ─────────────────────────
  if (isLoadingFilters) {
    return (
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div
          className={cn(
            !fromhomepage
              ? "lg:block container mx-auto px-4 py-3"
              : "lg:block container mx-auto px-0 py-3"
          )}
        >
          <div className="rounded-xl border border-border bg-card/95 shadow-sm p-3 md:p-4 space-y-3">
            <div
              className={cn(
                fromhomepage
                  ? "grid grid-cols-2 lg:grid-cols-4 gap-3"
                  : "grid lg:grid-flow-col-dense gap-3"
              )}
            >
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md hidden lg:block" />
            </div>
            <div className="flex items-center justify-between gap-3 pt-2">
              <Skeleton className="h-6 w-40 rounded-md" />
              <Skeleton className="h-10 w-28 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If filters failed to load for some reason
  if (!searchFilters) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
      {!fromhomepage && (
        <div className="lg:hidden block container mx-auto p-2 pb-3">
          <div className="flex items-center gap-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full p-2"
            >
              <HiArrowSmallLeft
                className="h-5 w-5"
                onClick={() => {
                  navigate(Paths.HOME);
                }}
              />
            </Button>
            {bookClass && (
              <div className="flex-1">
                {source && (
                  <div className="flex items-center text-md gap-x-2">
                    <span className="font-medium">{source}</span>
                    <HiOutlineArrowLongRight />
                    <span className="font-medium">{destination}</span>
                  </div>
                )}
                <div className="flex items-center text-2xs gap-x-1 text-muted-foreground">
                  {moment(departureDate).isValid() ? (
                    <>
                      <span>
                        {moment(departureDate).format("MMM DD, YYYY")}{" "}
                      </span>
                      <span className="block">•</span>
                    </>
                  ) : null}
                  <span>
                    {noOfPassengers}{" "}
                    {noOfPassengers == 1 ? " Passenger" : "Passengers"}
                    {", "}
                    {bookClass}
                  </span>
                  <span className="block">•</span>
                  <span>
                    {searchFilters.CabinClasses &&
                      searchFilters.CabinClasses.find(
                        (item) => item.Code == bookClass
                      ).ClassName}
                  </span>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full p-2-full"
              onClick={() =>
                setShowFilter((prevshowFilter) => !prevshowFilter)
              }
            >
              {!showFilter && !isDesktop ? (
                <HiOutlinePencilSquare className="h-5 w-5" />
              ) : (
                <IoMdCloseCircle className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* FORM CONTAINER */}
      <div
        className={cn(
          !showFilter && !isDesktop && !fromhomepage && "hidden",
          !fromhomepage
            ? "lg:block container mx-auto px-4 pb-3"
            : "lg:block container mx-auto px-0 pb-3"
        )}
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn(
              " bg-card/95",
              fromhomepage ? "mt-0" : "mt-2"
            )}
          >
            {/* TOP GRID: from / to / dates / pax */}
            <div
              className={cn(
                fromhomepage
                  ? "grid grid-cols-2 lg:grid-cols-4 dark:border rounded-md"
                  : "grid lg:grid-flow-col-dense gap-2 pt-3 px-3"
              )}
            >
              {!fromhomepage && (
                <FormField
                  control={form.control}
                  name="tripType"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        !fromhomepage
                          ? "flex flex-col"
                          : "flex flex-col p-3 border-b lg:border-e flex-1"
                      )}
                    >
                      {fromhomepage && <FormLabel>Trip Type</FormLabel>}
                      <Select
                        role="combobox"
                        value={field.value}
                        onValueChange={field.onChange}
                        className="font-medium"
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Flight Type" />
                          </SelectTrigger>
                        </FormControl>

                        <SelectContent>
                          {searchFilters.TripTypes &&
                            searchFilters.TripTypes.length > 0 &&
                            searchFilters.TripTypes.map((type) => {
                              if (type.Code === "multiple") return null; // Skip Multi-City
                              return (
                                <SelectItem
                                  key={type.Code + type.TripTypeID}
                                  value={type.TripTypeID.toString()}
                                >
                                  {type.TypeName}
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                ></FormField>
              )}

              {/* FROM / SWAP */}
              <div
                className={cn(
                  fromhomepage && "relative items-center",
                  !isDesktop && "flex-col items-center justify-center",
                  "flex"
                )}
              >
                <FormField
                  control={form.control}
                  name="from"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "flex flex-col flex-1 w-full",
                        fromhomepage &&
                        "px-3 pt-3 pb-2 border-b lg:border-e cursor-pointer hover:bg-primary/10 space-y-0"
                      )}
                    >
                      {fromhomepage && <FormLabel>From</FormLabel>}
                      {isDesktop ? (
                        <Popover
                          open={openOrigin}
                          onOpenChange={setOpenOrigin}
                          sideOffset={0}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={fromhomepage ? "ghost" : "outline"}
                                role="combobox"
                                className={cn(
                                  "justify-between hover:bg-transparent truncate",
                                  !field.value && "text-muted-foreground",
                                  fromhomepage &&
                                  "m-0 mt-0 px-0 hover:bg-transparent"
                                )}
                              >
                                <span className="flex flex-1 items-center truncate">
                                  <MdFlightTakeoff className="me-2" />
                                  <span>
                                    {!isEmpty(field.value)
                                      ? field.value
                                      : "Origin..."}
                                  </span>
                                </span>
                                {!fromhomepage && (
                                  <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>

                          <PopoverContent className="h-72 p-0 PopoverContent">
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder="Search airport..."
                                value={originSearch}
                                onValueChange={setOriginSearch}
                              />
                              <CommandList>
                                {isLoadingOrigin && <div className="p-2 text-sm text-center">Searching...</div>}
                                {!isLoadingOrigin && allOriginAirports.length === 0 && <CommandEmpty>No airports found.</CommandEmpty>}
                                <CommandGroup>
                                  {allOriginAirports.length > 0 &&
                                    allOriginAirports.map(
                                      (airport, i) => {
                                        return (
                                          <CommandItem
                                            key={airport.IATAcode + "_" + i}
                                            value={airport.SearchLabel}
                                            onSelect={() => {
                                              form.setValue(
                                                "from",
                                                airport.IATAcode
                                              );
                                              setOpenOrigin(false);
                                              setOpenDest(true);
                                            }}
                                          >
                                            <div className="flex items-center gap-2 py-1">
                                              <div className="flex items-center justify-center min-w-12 h-8 bg-slate-500 text-white rounded font-bold text-sm px-1">
                                                {airport.IATAcode}
                                              </div>
                                              <div className="flex flex-col leading-tight">
                                                <span className="font-bold text-sm">
                                                  {airport.AirportCity}, {airport.Country}
                                                </span>
                                                <span className="text-[13px] text-muted-foreground">
                                                  {airport.AirportName}
                                                </span>
                                              </div>
                                            </div>

                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value ===
                                                  airport.IATAcode
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                          </CommandItem>
                                        );
                                      }
                                    )}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Drawer
                          open={openOrigin}
                          onOpenChange={setOpenOrigin}
                        >
                          <DrawerTrigger asChild>
                            <FormControl>
                              <Button
                                variant={fromhomepage ? "ghost" : "outline"}
                                role="combobox"
                                className={cn(
                                  "justify-between hover:bg-transparent truncate",
                                  !field.value && "text-muted-foreground",
                                  fromhomepage &&
                                  "m-0 mt-0 px-0 hover:bg-transparent"
                                )}
                              >
                                <span className="flex flex-1 items-center truncate">
                                  <MdFlightTakeoff className="me-2" />
                                  <span>
                                    {!isEmpty(field.value)
                                      ? field.value
                                      : "Origin..."}
                                  </span>
                                </span>
                                {!fromhomepage && (
                                  <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                )}
                              </Button>
                            </FormControl>
                          </DrawerTrigger>

                          <DrawerContent>
                            <div className="mt-4 border-t">
                              <Command
                                className="w-full max-w-full"
                                shouldFilter={false}
                              >
                                <CommandInput
                                  placeholder="Search airport..."
                                  value={originSearch}
                                  onValueChange={setOriginSearch}
                                />
                                <CommandList>
                                  {isLoadingOrigin && <div className="p-2 text-sm text-center">Searching...</div>}
                                  {!isLoadingOrigin && allOriginAirports.length === 0 && <CommandEmpty>No airports found.</CommandEmpty>}
                                  <CommandGroup>
                                    {allOriginAirports.length > 0 &&
                                      allOriginAirports.map(
                                        (airport, i) => {
                                          return (
                                            <CommandItem
                                              key={airport.IATAcode + "_" + i}
                                              value={airport.SearchLabel}
                                              onSelect={() => {
                                                form.setValue(
                                                  "from",
                                                  airport.IATAcode
                                                );
                                                setOpenOrigin(false);
                                                setOpenDest(true);
                                              }}
                                            >
                                              <div className="flex items-center gap-2 py-1">
                                                <div className="flex items-center justify-center min-w-12 h-8 bg-slate-500 text-white rounded font-bold text-sm px-1">
                                                  {airport.IATAcode}
                                                </div>
                                                <div className="flex flex-col leading-tight">
                                                  <span className="font-bold text-sm">
                                                    {airport.AirportCity}, {airport.Country}
                                                  </span>
                                                  <span className="text-[13px] text-muted-foreground">
                                                    {airport.AirportName}
                                                  </span>
                                                </div>
                                              </div>

                                              <Check
                                                className={cn(
                                                  "mr-2 h-4 w-4",
                                                  field.value ===
                                                    airport.IATAcode
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                                )}
                                              />
                                            </CommandItem>
                                          );
                                        }
                                      )}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </div>
                          </DrawerContent>
                        </Drawer>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* SWAP BUTTON */}
                <div
                  className={cn(
                    fromhomepage && "absolute -right-4 -ms-2 z-10",
                    isDesktop ? "ms-2" : "mt-2 items-center",
                    "flex justify-center"
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={cn(
                      !fromhomepage && !isDesktop && "rotate-90",
                      "rounded-full shadow bg-background"
                    )}
                    onClick={() => {
                      let from = form.getValues().from;
                      let to = form.getValues().to;
                      form.setValue("to", from);
                      form.setValue("from", to);
                    }}
                  >
                    <IoMdSwap className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* TO */}
              <FormField
                control={form.control}
                name="to"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      "flex flex-col flex-1 w-full",
                      fromhomepage &&
                      "px-3 pt-3 pb-2 border-b lg:border-e cursor-pointer hover:bg-primary/10 space-y-0"
                    )}
                  >
                    {fromhomepage && <FormLabel>To</FormLabel>}
                    {isDesktop ? (
                      <Popover open={openDest} onOpenChange={setOpenDest} sideOffset={0}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={fromhomepage ? "ghost" : "outline"}
                              role="combobox"
                              className={cn(
                                "justify-between hover:bg-transparent truncate",
                                !field.value && "text-muted-foreground",
                                fromhomepage &&
                                "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                              )}
                            >
                              <span className="flex flex-1 items-center truncate">
                                <MdFlightLand className="me-2" />
                                <span>
                                  {!isEmpty(field.value)
                                    ? field.value
                                    : "Destination..."}
                                </span>
                              </span>
                              {!fromhomepage && (
                                <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="h-72 p-0 PopoverContent">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Search airport..."
                              value={destSearch}
                              onValueChange={setDestSearch}
                            />
                            <CommandList>
                              {isLoadingDest && <div className="p-2 text-sm text-center">Searching...</div>}
                              {!isLoadingDest && allDestAirports.length === 0 && <CommandEmpty>No airports found.</CommandEmpty>}
                              <CommandGroup>
                                {allDestAirports.length > 0 &&
                                  allDestAirports.map((airport, i) => {
                                    return (
                                      <CommandItem
                                        key={airport.IATAcode + "_" + i}
                                        value={airport.SearchLabel}
                                        onSelect={() => {
                                          form.setValue(
                                            "to",
                                            airport.IATAcode
                                          );
                                          setOpenDest(false);
                                        }}
                                      >
                                        <div className="flex items-center gap-2 py-1">
                                          <div className="flex items-center justify-center min-w-12 h-8 bg-slate-500 text-white rounded font-bold text-sm px-1">
                                            {airport.IATAcode}
                                          </div>
                                          <div className="flex flex-col leading-tight">
                                            <span className="font-bold text-sm">
                                              {airport.AirportCity}, {airport.Country}
                                            </span>
                                            <span className="text-[13px] text-muted-foreground">
                                              {airport.AirportName}
                                            </span>
                                          </div>
                                        </div>

                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === airport.IATAcode
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                      </CommandItem>
                                    );
                                  })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Drawer open={openDest} onOpenChange={setOpenDest}>
                        <DrawerTrigger asChild>
                          <FormControl>
                            <Button
                              variant={fromhomepage ? "ghost" : "outline"}
                              role="combobox"
                              className={cn(
                                "justify-between hover:bg-transparent",
                                !field.value && "text-muted-foreground",
                                fromhomepage &&
                                "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                              )}
                            >
                              <span className="flex flex-1 items-center truncate">
                                <MdFlightLand className="me-2" />
                                <span>
                                  {!isEmpty(field.value)
                                    ? field.value
                                    : "Destination..."}
                                </span>
                              </span>
                              {!fromhomepage && (
                                <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              )}
                            </Button>
                          </FormControl>
                        </DrawerTrigger>
                        <DrawerContent>
                          <div className="mt-4 border-t">
                            <Command
                              className="w-full max-w-full"
                              shouldFilter={false}
                            >
                              <CommandInput
                                placeholder="Search airport..."
                                value={destSearch}
                                onValueChange={setDestSearch}
                              />
                              <CommandList>
                                {isLoadingDest && <div className="p-2 text-sm text-center">Searching...</div>}
                                {!isLoadingDest && allDestAirports.length === 0 && <CommandEmpty>No airports found.</CommandEmpty>}
                                <CommandGroup>
                                  {allDestAirports.length > 0 &&
                                    allDestAirports.map(
                                      (airport, i) => {
                                        return (
                                          <CommandItem
                                            key={airport.IATAcode + "_" + i}
                                            value={airport.SearchLabel}
                                            onSelect={() => {
                                              form.setValue(
                                                "to",
                                                airport.IATAcode
                                              );
                                              setOpenDest(false);
                                            }}
                                          >
                                            <div className="flex items-center gap-2 py-1">
                                              <div className="flex items-center justify-center min-w-12 h-8 bg-slate-500 text-white rounded font-bold text-sm px-1">
                                                {airport.IATAcode}
                                              </div>
                                              <div className="flex flex-col leading-tight">
                                                <span className="font-bold text-sm">
                                                  {airport.AirportCity}, {airport.Country}
                                                </span>
                                                <span className="text-[13px] text-muted-foreground">
                                                  {airport.AirportName}
                                                </span>
                                              </div>
                                            </div>

                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value ===
                                                  airport.IATAcode
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                          </CommandItem>
                                        );
                                      }
                                    )}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </div>
                        </DrawerContent>
                      </Drawer>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* DEPARTURE DATE */}
              <FormField
                control={form.control}
                name="departure"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      "flex flex-col",
                      fromhomepage &&
                      "px-3 pt-3 pb-2 border-b lg:border-e cursor-pointer hover:bg-primary/10 space-y-0"
                    )}
                  >
                    {fromhomepage && <FormLabel>Departure</FormLabel>}
                    {isDesktop ? (
                      <Popover
                        open={isCalenderOpen}
                        onOpenChange={setIsCalenderOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={fromhomepage ? "ghost" : "outline"}
                              role="combobox"
                              className={cn(
                                "justify-between hover:bg-transparent",
                                !field.value && "text-muted-foreground",
                                fromhomepage &&
                                "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                              )}
                            >
                              <span className="flex flex-1 items-center truncate">
                                <IoMdCalendar className="me-2" />
                                <span>
                                  {field.value ? (
                                    format(field.value, "PP")
                                  ) : (
                                    <span>Departure</span>
                                  )}
                                </span>
                              </span>
                              {!fromhomepage && (
                                <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            captionLayout="dropdown-buttons"
                            fromYear={new Date().getFullYear()}
                            toYear={new Date().getFullYear() + 1}
                            initialFocus
                            selected={field.value ? new Date(field.value) : undefined}
                            defaultMonth={field.value || new Date()}
                            onSelect={(e) => {
                              setIsCalenderOpen(false);
                              field.onChange(e);
                            }}
                            disabled={(date) =>
                              date <
                              new Date(
                                new Date().setDate(
                                  new Date().getDate() - 1
                                )
                              )
                            }
                          />
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Drawer
                        open={isCalenderOpen}
                        onOpenChange={setIsCalenderOpen}
                      >
                        <DrawerTrigger asChild>
                          <FormControl>
                            <Button
                              variant={fromhomepage ? "ghost" : "outline"}
                              role="combobox"
                              className={cn(
                                "justify-between hover:bg-transparent",
                                !field.value && "text-muted-foreground",
                                fromhomepage &&
                                "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                              )}
                            >
                              <span className="flex flex-1 items-center truncate">
                                <IoMdCalendar className="me-2" />
                                <span>
                                  {field.value ? (
                                    format(field.value, "PP")
                                  ) : (
                                    <span>Departure</span>
                                  )}
                                </span>
                              </span>
                              {!fromhomepage && (
                                <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              )}
                            </Button>
                          </FormControl>
                        </DrawerTrigger>
                        <DrawerContent className="flex items-center justify-center space-x-2">
                          <Calendar
                            mode="single"
                            captionLayout="dropdown-buttons"
                            fromYear={new Date().getFullYear()}
                            toYear={new Date().getFullYear() + 1}
                            selected={field.value ? new Date(field.value) : undefined}
                            defaultMonth={field.value || new Date()}
                            onSelect={(e) => {
                              setIsCalenderOpen(false);
                              field.onChange(e);
                            }}
                            disabled={(date) =>
                              date <
                              new Date(
                                new Date().setDate(
                                  new Date().getDate() - 1
                                )
                              )
                            }
                            initialFocus
                          />
                        </DrawerContent>
                      </Drawer>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* RETURN DATE */}
              {form.getValues().tripType == "2" && (
                <FormField
                  control={form.control}
                  name="return"
                  render={({ field }) => (
                    <FormItem
                      className={cn(
                        "flex flex-col",
                        fromhomepage &&
                        "px-3 pt-3 pb-2 border-b lg:border-e cursor-pointer hover:bg-primary/10 space-y-0"
                      )}
                    >
                      {fromhomepage && <FormLabel>Return</FormLabel>}
                      {isDesktop ? (
                        <Popover
                          open={
                            isCalenderOpenReturn &&
                            form.getValues("departure")
                          }
                          onOpenChange={setIsCalenderOpenReturn}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={fromhomepage ? "ghost" : "outline"}
                                role="combobox"
                                className={cn(
                                  "justify-between",
                                  !field.value && "text-muted-foreground",
                                  fromhomepage &&
                                  "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                                )}
                              >
                                <span className="flex flex-1 items-center truncate">
                                  <IoMdCalendar className="me-2" />
                                  <span>
                                    {field.value ? (
                                      format(field.value, "PP")
                                    ) : (
                                      <span>Return</span>
                                    )}
                                  </span>
                                </span>
                                {!fromhomepage && (
                                  <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown-buttons"
                              fromYear={new Date().getFullYear()}
                              toYear={new Date().getFullYear() + 1}
                              selected={field.value ? new Date(field.value) : undefined}
                              defaultMonth={field.value || form.getValues("departure") || new Date()}
                              onSelect={(e) => {
                                setIsCalenderOpenReturn(false);
                                field.onChange(e);
                              }}
                              disabled={(date) => {
                                var dt = new Date(
                                  form.getValues("departure")
                                );
                                return (
                                  date <=
                                  new Date(
                                    dt.setDate(dt.getDate() - 1)
                                  )
                                );
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Drawer
                          open={isCalenderOpenReturn}
                          onOpenChange={setIsCalenderOpenReturn}
                        >
                          <DrawerTrigger asChild>
                            <FormControl>
                              <Button
                                variant={fromhomepage ? "ghost" : "outline"}
                                role="combobox"
                                className={cn(
                                  "justify-between",
                                  !field.value && "text-muted-foreground",
                                  fromhomepage &&
                                  "m-0 mt-0 px-0 hover:bg-transparent space-y-0"
                                )}
                              >
                                <span className="flex flex-1 items-center truncate">
                                  <IoMdCalendar className="me-2" />
                                  <span>
                                    {field.value ? (
                                      format(field.value, "PP")
                                    ) : (
                                      <span>Return</span>
                                    )}
                                  </span>
                                </span>
                                {!fromhomepage && (
                                  <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                )}
                              </Button>
                            </FormControl>
                          </DrawerTrigger>
                          <DrawerContent className="w-auto p-0 flex items-center justify-center space-x-2">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown-buttons"
                              fromYear={new Date().getFullYear()}
                              toYear={new Date().getFullYear() + 1}
                              selected={field.value ? new Date(field.value) : undefined}
                              defaultMonth={field.value || form.getValues("departure") || new Date()}
                              onSelect={(e) => {
                                setIsCalenderOpenReturn(false);
                                field.onChange(e);
                              }}
                              disabled={(date) => {
                                var dt = new Date(
                                  form.getValues("departure")
                                );
                                return (
                                  date <=
                                  new Date(
                                    dt.setDate(dt.getDate() - 1)
                                  )
                                );
                              }}
                              initialFocus
                            />
                          </DrawerContent>
                        </Drawer>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* PASSENGERS & CLASS */}
              <FormItem
                className={cn(
                  "flex flex-col",
                  fromhomepage &&
                  "px-3 pt-3 pb-2 border-b lg:border-e cursor-pointer hover:bg-primary/10 space-y-0"
                )}
              >
                {fromhomepage && <FormLabel>Passenger</FormLabel>}
                {isDesktop ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={fromhomepage ? "ghost" : "outline"}
                          role="combobox"
                          className={cn(
                            "justify-between",
                            !noOfPassengers && "text-muted-foreground",
                            fromhomepage &&
                            "m-0 px-0 hover:bg-transparent"
                          )}
                        >
                          <span className="flex flex-1 items-center truncate">
                            <FaPersonWalkingLuggage className="me-2" />
                            <span>
                              {noOfPassengers ? (
                                <>
                                  {noOfPassengers} Passenger
                                  {noOfPassengers > 1 ? "s" : ""}
                                  {", "}
                                  {searchFilters.CabinClasses &&
                                    searchFilters.CabinClasses.find(
                                      (item) => item.Code == bookClass
                                    ).ClassName}
                                </>
                              ) : (
                                <span>Select Passengers</span>
                              )}
                            </span>
                          </span>
                          {!fromhomepage && (
                            <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className="py-3 space-y-3">
                        <div className="w-full mb-3">
                          <div className="relative">
                            <FormField
                              control={form.control}
                              name="nationality"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormControl>
                                    <Select
                                      placeholder="select flight type"
                                      value={field.value}
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select Nationality" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {searchFilters.Countries &&
                                          searchFilters.Countries.length > 0 &&
                                          searchFilters.Countries.map(
                                            (nt) => {
                                              return (
                                                <SelectItem
                                                  value={nt.CountryCode}
                                                  key={nt.CountryCode}
                                                >
                                                  {nt.Demonym}
                                                </SelectItem>
                                              );
                                            }
                                          )}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            ></FormField>
                          </div>
                        </div>

                        {/* Adult */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Adult
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="noOfPassenger"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (
                                          field.value > 1 &&
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          field.value
                                        )
                                          form.setValue(
                                            "noOfPassenger",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        form.setValue(
                                          "noOfPassenger",
                                          field.value + 1
                                        );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Child */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Child
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="child"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (field.value > 0)
                                          form.setValue(
                                            "child",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        if (
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          form.getValues("noOfPassenger")
                                        )
                                          form.setValue(
                                            "child",
                                            field.value + 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Infant */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Infant
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="infant"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (field.value > 0)
                                          form.setValue(
                                            "infant",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        if (
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          form.getValues("noOfPassenger")
                                        )
                                          form.setValue(
                                            "infant",
                                            field.value + 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Cabin class */}
                        <FormField
                          control={form.control}
                          name="cabinClass"
                          render={({ field }) => (
                            <FormItem className="space-y-3">
                              <FormLabel>Select Class</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex flex-col space-y-1"
                                >
                                  {searchFilters.CabinClasses.map((cc) => {
                                    if (cc.ClassType == "Flight") {
                                      return (
                                        <FormItem
                                          className="flex items-center space-x-3 space-y-0"
                                          key={cc.CabinID}
                                        >
                                          <FormControl>
                                            <RadioGroupItem
                                              value={cc.Code}
                                              id={cc.CabinID}
                                            />
                                          </FormControl>
                                          <FormLabel
                                            className="font-normal"
                                            htmlFor={cc.CabinID}
                                          >
                                            {cc.ClassName}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }
                                    return null;
                                  })}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Drawer>
                    <DrawerTrigger asChild>
                      <FormControl>
                        <Button
                          variant={fromhomepage ? "ghost" : "outline"}
                          role="combobox"
                          className={cn(
                            "justify-between",
                            (!noOfPassengers && "text-muted-foreground") ||
                            (fromhomepage &&
                              "m-0 px-0 hover:bg-transparent")
                          )}
                        >
                          <span className="flex flex-1 items-center truncate">
                            <FaPersonWalkingLuggage className="me-2" />
                            <span>
                              {noOfPassengers ? (
                                <>
                                  {noOfPassengers} Passenger
                                  {noOfPassengers > 1 ? "s" : ""}
                                </>
                              ) : (
                                <span>Select Passengers</span>
                              )}
                            </span>
                          </span>
                          {!fromhomepage && (
                            <LuChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          )}
                        </Button>
                      </FormControl>
                    </DrawerTrigger>
                    <DrawerContent className="w-auto p-5">
                      <div className="py-3 space-y-3">
                        <div className="w-full mb-3">
                          <div className="relative">
                            <FormField
                              control={form.control}
                              name="nationality"
                              render={({ field }) => (
                                <FormItem className="flex flex-col">
                                  <FormControl>
                                    <Select
                                      placeholder="select flight type"
                                      value={field.value}
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select Nationality" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {searchFilters.Countries &&
                                          searchFilters.Countries.length > 0 &&
                                          searchFilters.Countries.map(
                                            (nt) => {
                                              return (
                                                <SelectItem
                                                  value={nt.CountryCode}
                                                  key={nt.CountryCode}
                                                >
                                                  {nt.Demonym}
                                                </SelectItem>
                                              );
                                            }
                                          )}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            ></FormField>
                          </div>
                        </div>

                        {/* Adult */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Adult
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="noOfPassenger"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (
                                          field.value > 1 &&
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          field.value
                                        )
                                          form.setValue(
                                            "noOfPassenger",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        form.setValue(
                                          "noOfPassenger",
                                          field.value + 1
                                        );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Child */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Child
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="child"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (field.value > 0)
                                          form.setValue(
                                            "child",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        if (
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          form.getValues("noOfPassenger")
                                        )
                                          form.setValue(
                                            "child",
                                            field.value + 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Infant */}
                        <div
                          className="mb-3 px-3 rounded border border-input"
                          data-hs-input-number
                        >
                          <div className="w-full flex justify-between items-center gap-x-3">
                            <div>
                              <span className="block font-medium text-sm text-foreground">
                                Infant
                              </span>
                            </div>
                            <FormField
                              control={form.control}
                              name="infant"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex items-center gap-x-1.5">
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-decrement
                                      onClick={() => {
                                        if (field.value > 0)
                                          form.setValue(
                                            "infant",
                                            field.value - 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                      </svg>
                                    </Button>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="p-0 w-6 bg-transparent border-0 text-center focus:ring-0"
                                      />
                                    </FormControl>
                                    <Button
                                      className="w-6 h-6 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-input bg-background text-foreground shadow-sm hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
                                      data-hs-input-number-increment
                                      onClick={() => {
                                        if (
                                          form.getValues("child") +
                                          form.getValues("infant") <
                                          form.getValues("noOfPassenger")
                                        )
                                          form.setValue(
                                            "infant",
                                            field.value + 1
                                          );
                                      }}
                                    >
                                      <svg
                                        className="flex-shrink-0 w-3.5 h-3.5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M5 12h14" />
                                        <path d="M12 5v14" />
                                      </svg>
                                    </Button>
                                    <FormMessage />
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Cabin class */}
                        <FormField
                          control={form.control}
                          name="cabinClass"
                          render={({ field }) => (
                            <FormItem className="space-y-3">
                              <FormLabel>Select Class</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex flex-col space-y-1"
                                >
                                  {searchFilters.CabinClasses.map((cc) => {
                                    if (cc.ClassType == "Flight") {
                                      return (
                                        <FormItem
                                          className="flex items-center space-x-3 space-y-0"
                                          key={cc.CabinID}
                                        >
                                          <FormControl>
                                            <RadioGroupItem
                                              value={cc.Code}
                                              id={`mobile-cabin-${cc.CabinID}`}
                                            />
                                          </FormControl>
                                          <FormLabel
                                            className="font-normal"
                                            htmlFor={`mobile-cabin-${cc.CabinID}`}
                                          >
                                            {cc.ClassName}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }
                                    return null;
                                  })}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </DrawerContent>
                  </Drawer>
                )}
              </FormItem>
            </div>

            {/* FARE TYPE + SUBMIT ROW */}
            {
              <div className="px-3 pb-3 pt-2">
                <div className="grid gap-4 md:grid-cols-[minmax(0,4fr)_minmax(0,1.2fr)] items-end">
                  <div
                    className={cn(
                      "w-full",
                      fromhomepage ? "pt-1" : "py-2"
                    )}
                  >
                    {!(isDomestic && !fromhomepage) && (
                      <FormField
                        control={form.control}
                        name="fareType"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-base font-medium">
                              Select a special fare (Extra Savings)
                            </FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={handlePaxFareTypeChange}
                                defaultValue={field.value?.toString()}
                                className="flex flex-col sm:flex-row sm:flex-wrap gap-3"
                              >
                                {searchFilters.FareTypes &&
                                  searchFilters.FareTypes.length > 0 &&
                                  searchFilters.FareTypes.map((pft) => {
                                    return (
                                      <FormItem
                                        className="flex items-center space-x-2 space-y-0"
                                        key={pft.TypeID}
                                      >
                                        <FormControl>
                                          <RadioGroupItem
                                            value={pft.TypeID.toString()}
                                            id={`fareType-main-${pft.TypeID}`}
                                          />
                                        </FormControl>
                                        <FormLabel
                                          className="font-normal cursor-pointer text-sm"
                                          htmlFor={`fareType-main-${pft.TypeID}`}
                                        >
                                          {pft.Title}
                                        </FormLabel>
                                      </FormItem>
                                    );
                                  })}
                              </RadioGroup>
                            </FormControl>

                            {selectedPaxFareType &&
                              selectedPaxFareType.DisplayMessage && (
                                <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                                  <div className="flex items-start gap-2">
                                    <svg
                                      className="h-5 w-5 text-amber-500 mt-0.5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <p className="text-xs sm:text-sm text-amber-900 font-medium">
                                      {selectedPaxFareType.DisplayMessage}
                                    </p>
                                  </div>
                                </div>
                              )}

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <div className="flex justify-end items-end">
                    <Button
                      type="submit"
                      className={cn(
                        "w-full md:w-auto h-10 px-4 text-sm font-medium",
                        fromhomepage &&
                        "w-full md:w-auto py-4 "
                      )}
                    >
                      {fromhomepage ? "Search flight" : "Search"}
                    </Button>
                  </div>
                </div>
              </div>
            }
          </form>
        </Form>
      </div>
    </div>
  );
}
